"use client";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@/components/library/Icon";
import { useOrg } from "@/hooks/useOrg";
import { PhoneScript } from "@/types/campaigns";
import { UseCase } from "@/types/scripts";
import { apiLive, apiFetch } from "@/lib/api";
import { createMinDurationEnforcer } from "@/utils/onboarding/common";
import { loadAssociatedUseCases } from "@/utils/onboarding/onboardingScriptApi";
import { scriptApiVersionFromFeatures } from "@/utils/org/scriptsApiVersion";
import { getScriptDetailUrl } from "@/utils/scriptVersioning/api";
import { OnboardingStepHeader } from "./OnboardingStepHeader";
import { ScriptBuildingLoader } from "./ScriptBuildingLoader";
import { ScriptPreviewSection } from "./ScriptPreviewSection";
import { StepErrorState } from "./StepErrorState";
import { StepFooter } from "./StepFooter";

interface AfterHoursStepProps {
  readonly onBack?: () => void;
  readonly onNext: () => void;
}

type AfterHoursOption = "voicemail" | "continue" | "custom";

interface OptionCard {
  key: AfterHoursOption;
  icon: React.ReactNode;
  recommended?: boolean;
}

const OPTIONS: OptionCard[] = [
  {
    key: "voicemail",
    icon: <Icon icon="comment-dots" className="w-6 h-6" />,
    recommended: true,
  },
  {
    key: "continue",
    icon: <Icon icon="moon" className="w-6 h-6" />,
  },
  {
    key: "custom",
    icon: <Icon icon="phone-slash" className="w-6 h-6" />,
  },
];

type GenerationStatus =
  | { state: "idle" }
  | { state: "generating" }
  | { state: "error" }
  | { state: "ready"; script: PhoneScript; useCases: UseCase[]; scriptId: string };

const AFTER_HOURS_WORKSTREAM = [
  "loadBusinessSchedule(config.hours)",
  "detectOffHoursWindows(schedule)",
  "initAfterHoursTemplate({ mode: 'voicemail' })",
  "composeVoicemailGreeting(org.name)",
  "configureRoutingRules(afterHours)",
  "setEscalationPolicy({ priority: 'urgent' })",
  "validateCallFlow(afterHoursTree)",
  "finalizeAfterHoursBundle()",
  "registerActivationHook(bundle)",
  "exportConfig({ format: 'v1' })",
];

export function AfterHoursStep({ onBack, onNext }: AfterHoursStepProps) {
  const { t } = useTranslation("onboarding");
  const { org, saveOrg } = useOrg();

  const VALID_OPTIONS: AfterHoursOption[] = ["voicemail", "continue", "custom"];
  const rawOption = org?.details?.onboarding?.afterHoursOption as string | undefined;
  const existingOption = VALID_OPTIONS.includes(rawOption as AfterHoursOption)
    ? (rawOption as AfterHoursOption)
    : undefined;
  const existingScriptId = org?.details?.onboarding?.afterHoursScriptId;

  const [selectedOption, setSelectedOption] = useState<AfterHoursOption>(
    existingOption ?? "voicemail"
  );
  const [customInstructions, setCustomInstructions] = useState(
    org?.details?.onboarding?.afterHoursCustomInstructions ?? ""
  );
  const [generationStatus, setGenerationStatus] = useState<GenerationStatus>({ state: "idle" });

  const isCustomWithoutInstructions = selectedOption === "custom" && !customInstructions.trim();

  // If we already generated an after-hours script, fetch it on mount instead of regenerating
  useEffect(() => {
    if (!existingScriptId || generationStatus.state !== "idle") return;

    let cancelled = false;
    const fetchExistingScript = async () => {
      setGenerationStatus({ state: "generating" });
      try {
        const version = scriptApiVersionFromFeatures(org?.features);
        const script = await apiLive<PhoneScript>(
          getScriptDetailUrl(existingScriptId, version)
        );
        if (cancelled || !script) return;

        const fetchedUseCases = await loadAssociatedUseCases(script, version);
        if (cancelled) return;

        setGenerationStatus({
          state: "ready",
          script,
          useCases: fetchedUseCases,
          scriptId: existingScriptId,
        });
      } catch {
        if (!cancelled) setGenerationStatus({ state: "idle" });
      }
    };

    fetchExistingScript();
    return () => {
      cancelled = true;
    };
  }, [existingScriptId, org?.features]);

  const handleSave = useCallback(async () => {
    // If we already have a script for the same option, skip regeneration
    if (existingScriptId && selectedOption === existingOption) {
      if (selectedOption === "continue") {
        onNext();
        return;
      }
      // Script is already loaded in generationStatus — just proceed
      if (generationStatus.state === "ready") {
        onNext();
        return;
      }
    }

    setGenerationStatus({ state: "generating" });
    const enforceMinDuration = createMinDurationEnforcer(10_000);

    try {
      const response = await apiFetch<{
          success: boolean;
          scriptId?: string | null;
          option: AfterHoursOption;
          script?: PhoneScript;
          useCases?: UseCase[];
        }>(
        "/api/onboarding/after-hours-script",
        "POST",
        {
        body: {
        option: selectedOption,
        ...(selectedOption === "custom" && customInstructions.trim()
          ? { customInstructions: customInstructions.trim() }
          : {}),
      },
      },
      );

      if (!response?.success) {
        throw new Error("Failed to generate after-hours script");
      }

      // Save the selected option and script ID to org onboarding details
      await saveOrg(
        {
          details: {
            ...org?.details,
            onboarding: {
              status: org?.details?.onboarding?.status ?? "in_progress",
              maxCompletedStep: org?.details?.onboarding?.maxCompletedStep ?? 1,
              ...org?.details?.onboarding,
              afterHoursOption: selectedOption,
              afterHoursScriptId: response.scriptId ?? undefined,
              afterHoursCustomInstructions:
                selectedOption === "custom" ? customInstructions.trim() : undefined,
            },
          },
        },
        { silent: true }
      );

      await enforceMinDuration();

      // "continue" means keep the same script — no separate after-hours script to review
      if (selectedOption === "continue" || !response.script || !response.scriptId) {
        onNext();
        return;
      }

      const version = scriptApiVersionFromFeatures(org?.features);
      const fetchedUseCases =
        response.useCases ?? (await loadAssociatedUseCases(response.script, version));

      setGenerationStatus({
        state: "ready",
        script: response.script,
        useCases: fetchedUseCases,
        scriptId: response.scriptId,
      });
    } catch (error) {
      console.error("Failed to save after-hours settings", error);
      await enforceMinDuration();
      setGenerationStatus({ state: "error" });
    }
  }, [
    onNext,
    org?.details,
    saveOrg,
    selectedOption,
    customInstructions,
    existingScriptId,
    existingOption,
    generationStatus.state,
    org?.features,
  ]);

  const handleScriptUpdate = useCallback((updatedScript: PhoneScript) => {
    setGenerationStatus((prev) => {
      if (prev.state === "ready") {
        return { ...prev, script: updatedScript };
      }
      return prev;
    });
  }, []);

  const handleUseCasesUpdate = useCallback((updatedUseCases: UseCase[]) => {
    setGenerationStatus((prev) => {
      if (prev.state === "ready") {
        return { ...prev, useCases: updatedUseCases };
      }
      return prev;
    });
  }, []);

  // Show loading state while generating
  if (generationStatus.state === "generating") {
    return (
      <ScriptBuildingLoader
        title={t("afterHours.generatingTitle")}
        subtitle={t("afterHours.generatingDescription")}
        messages={t("afterHours.buildingLoader.messages", { returnObjects: true }) as string[]}
        codeLines={AFTER_HOURS_WORKSTREAM}
        metricLabels={
          t("afterHours.buildingLoader.metrics", { returnObjects: true }) as [
            string,
            string,
            string,
          ]
        }
      />
    );
  }

  // Show script review after generation (voicemail/custom only)
  if (generationStatus.state === "ready") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-8">
          <div className="w-full max-w-4xl mx-auto space-y-6">
            <OnboardingStepHeader
              title={t("afterHours.reviewTitle")}
              description={t("afterHours.reviewDescription")}
            />

            <ScriptPreviewSection
              script={generationStatus.script}
              useCases={generationStatus.useCases}
              onScriptUpdate={handleScriptUpdate}
              onUseCasesUpdate={handleUseCasesUpdate}
            />
          </div>
        </div>

        <StepFooter onBack={onBack} onSkip={onNext} onNext={onNext} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-8 flex flex-col items-center justify-center">
        <div className="w-full max-w-2xl space-y-8">
          {/* Header */}
          <OnboardingStepHeader
            title={t("afterHours.title")}
            description={t("afterHours.subtitle")}
          />

          {/* Error state */}
          {generationStatus.state === "error" && (
            <StepErrorState
              message={t("afterHours.generationError")}
              onRetry={() => setGenerationStatus({ state: "idle" })}
              retryLabel={t("afterHours.retryButton")}
            />
          )}

          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {OPTIONS.map((option) => {
              const isSelected = selectedOption === option.key;

              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setSelectedOption(option.key);
                  }}
                  className={clsx(
                    "relative flex flex-col items-center gap-4 p-6 rounded-2xl border-2 transition-all duration-200",
                    "hover:shadow-md",
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-200 bg-panel hover:border-300"
                  )}
                >
                  {/* Recommended badge */}
                  {option.recommended && (
                    <span
                      className={clsx(
                        "absolute -top-2 left-1/2 -translate-x-1/2",
                        "px-2 py-0.5 rounded-full text-xs font-medium",
                        "bg-primary text-white"
                      )}
                    >
                      {t("afterHours.recommended")}
                    </span>
                  )}

                  {/* Icon */}
                  <div
                    className={clsx(
                      "w-14 h-14 rounded-xl flex items-center justify-center",
                      isSelected ? "bg-primary/10 text-primary" : "bg-100 text-muted-foreground"
                    )}
                  >
                    {option.icon}
                  </div>

                  {/* Title */}
                  <h3
                    className={clsx(
                      "text-base font-semibold",
                      isSelected ? "text-foreground" : "text-700"
                    )}
                  >
                    {t(`afterHours.options.${option.key}.title`)}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-muted-foreground text-center">
                    {t(`afterHours.options.${option.key}.description`)}
                  </p>

                  {/* Selection indicator */}
                  <div
                    className={clsx(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                      isSelected ? "border-primary bg-primary" : "border-300 bg-transparent"
                    )}
                  >
                    {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Custom instructions input */}
          <AnimatePresence>
            {selectedOption === "custom" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    {t("afterHours.customInstructionsLabel")}
                  </label>
                  <textarea
                    className={clsx(
                      "w-full min-h-[100px] rounded-xl border border-200 bg-panel p-3",
                      "text-sm text-foreground placeholder:text-muted-foreground",
                      "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary",
                      "resize-y"
                    )}
                    placeholder={t("afterHours.customInstructionsPlaceholder")}
                    value={customInstructions}
                    onChange={(e) => setCustomInstructions(e.target.value)}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Buttons */}
      <StepFooter
        onBack={onBack}
        onSkip={onNext}
        onNext={handleSave}
        nextDisabled={isCustomWithoutInstructions}
      />
    </div>
  );
}
